import React, { useEffect } from "react";
import moment from "moment";
import { Button, Col, DatePicker, Form, Input, message, Modal, Row, Select } from "antd";
import {
  displayFormat,
  infinityDateHttpFormat,
  infinityStartDateHttpFormat,
  toHttpFormat,
  toHttpFormatStartOfDay,
  classNames,
  selectFilterOption,
  getSelectOptions,
} from "../../../lib/utils";
import { createDicTermin, fetchDictionary, fetchMaxTeminId } from "../../../store/slices/dicts";
import "./TerminCreateModal.less";
import { DictionariesSearchParams } from "../../../lib/types/DictionariesSearchParams";
import { DictionaryTypeAlias } from "../../../lib/types/DictionaryTypeAlias";
import { useAppDispatch, useAppSelector } from "../../..";
import { DictionaryDto } from "../../../lib/interfaces/DictionaryDto";

interface TerminCreateModalProps {
  dicTypeAlias: DictionaryTypeAlias;
  open: boolean;
  closeModal: () => void;
  dictionariesSearchParams: DictionariesSearchParams;
  parentDicTypeAlias: DictionaryTypeAlias | null;
  selectedParentDicId?: number;
}

export const TerminCreateModal = (props: TerminCreateModalProps) => {
  const {
    dicTypeAlias,
    open,
    closeModal,
    dictionariesSearchParams,
    parentDicTypeAlias,
    selectedParentDicId,
  } = props;

  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  // Установка значения выбранного родительского словаря parentDicId, по какой-то причине именно Select с initialValue работает некорректно
  useEffect(() => {
    form.setFieldsValue({ parentDicId: selectedParentDicId });
  }, [selectedParentDicId]);

  const { activeDictionary, maxTeminId, parentDictionary } = useAppSelector((state) => state.dicts);
  const validateMessages = {
    required: "Обязательно для заполнения",
  };

  const onFinish = async (values: DictionaryDto) => {
    const isTerminExists = activeDictionary?.some(({ name }) => values.name === name);
    if (isTerminExists) {
      return message.warning("Термин с таким наименованием уже существует");
    }

    dispatch(
      createDicTermin({
        id: maxTeminId + 1,
        fromDate: toHttpFormatStartOfDay(values.fromDate),
        toDate: toHttpFormat(values.toDate),
        dicTypeAlias,
        name: values.name,
        abbrev: values.abbrev,
        dsc: values.dsc,
        parentDicId: values.parentDicId,
        alias: values.alias,
      })
    ).then(() => {
      dispatch(fetchMaxTeminId({ dicTypeAlias }));
      dispatch(
        fetchDictionary({
          dicTypeAlias,
          includeClosed: dictionariesSearchParams.includeClosed,
          onDate: dictionariesSearchParams.fromDate,
        })
      );
      form.resetFields();
    });

    closeModal();
  };

  const formItemClassName = classNames([
    "form-item-required__hidden-error",
    "termin-create-modal__form-item",
  ]);

  return (
    <Modal visible={open} footer={null} onCancel={closeModal} className="termin-create-modal">
      <h1 className="termin-create-modal__title">Новый термин</h1>
      <div className="test" />
      <Row>
        <Form
          form={form}
          validateMessages={validateMessages}
          initialValues={{
            fromDate: moment(infinityStartDateHttpFormat),
            toDate: moment(infinityDateHttpFormat),
          }}
          onFinish={onFinish}
          className="termin-create-modal__form"
          labelCol={{ span: 6 }}
        >
          <Form.Item
            className={formItemClassName}
            label="Наименование"
            name="name"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Аббревиатура" className={formItemClassName} name="abbrev">
            <Input maxLength={20} />
          </Form.Item>
          <Form.Item
            className={formItemClassName}
            name="parentDicId"
            label="Группа"
            rules={[
              {
                required: Boolean(parentDicTypeAlias),
              },
            ]}
          >
            <Select
              allowClear
              showSearch
              filterOption={selectFilterOption}
              options={getSelectOptions({
                list: parentDictionary,
              })}
              disabled={!parentDicTypeAlias}
            />
          </Form.Item>
          <Form.Item label="Alias" className={formItemClassName} name="alias">
            <Input maxLength={20} />
          </Form.Item>
          <Form.Item label="Комментарий" className={formItemClassName} name="dsc">
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item
            label="Период с"
            className={formItemClassName}
            name="fromDate"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <DatePicker format={displayFormat} allowClear={false} />
          </Form.Item>
          <Form.Item label="Период до" className={formItemClassName} name="toDate">
            <DatePicker
              disabledDate={(current) => current.isAfter(infinityDateHttpFormat)}
              format={displayFormat}
              allowClear={false}
              disabled
            />
          </Form.Item>

          <Row align="middle" justify="center" gutter={20}>
            <Col>
              <Button
                onClick={() => {
                  form.resetFields();
                  closeModal();
                }}
              >
                Отмена
              </Button>
            </Col>
            <Col>
              <Button type="primary" htmlType="submit">
                Сохранить
              </Button>
            </Col>
          </Row>
        </Form>
      </Row>
    </Modal>
  );
};
